import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import LockIcon from '@material-ui/icons/Lock'
import React, { useEffect, useRef, useState } from 'react'
import { makeStyles, Typography, useIsMobile, Tooltip } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import classNames from 'classnames'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import type { CalendarEvent } from 'paintscout'
import { getCrews } from '@paintscout/util/builder/options'
import moment from 'moment'
import ProductionEventPopper from './ProductionEventPopper'
import { getCrewPalette, getProjectTypeLabel } from './util'
import type { CrewPalette } from './util'
import { useProductionCalendar } from './ProductionCalendarContext'
import { useProductionEventPermissions } from 'shared/hooks'
import type { ZoomLevel } from './TimelineHeader'

const useStyles = makeStyles<Theme, CrewPalette>(
  (theme) => ({
    root: {
      height: 66, // matches the design spec (W 255px / H 66px)
      cursor: 'pointer',
      transition: 'all 0.2s',
      position: 'relative',
      overflow: 'visible'
    },
    jobBlockContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'visible'
    },
    jobBlock: {
      height: '100%',
      borderRadius: theme.borderRadius.md,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: ({ surface }) => surface
    },
    viewOnlyOverlay: {
      // Faint diagonal-stripe wash for the locked/disabled state.
      // Mirrors the integrationOverlay treatment in Calendar/day/DayEvent.tsx.
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1,
        borderRadius: 'inherit',
        backgroundImage: `
          repeating-linear-gradient(
            135deg,
            rgba(0,0,0,0.04),
            rgba(0,0,0,0.04) 10px,
            transparent 10px,
            transparent 13px
          )
        `
      }
    },
    lockIcon: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1.25),
      width: 16,
      height: 16,
      color: ({ accent }) => accent,
      zIndex: 1700,
      pointerEvents: 'none'
    },
    titleLabel: {
      color: ({ accent }) => accent,
      fontSize: '1rem',
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0,
      flex: 1
    },
    subtitleLabel: {
      color: ({ accentSoft }) => accentSoft,
      fontSize: '0.875rem',
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0,
      display: 'block'
    },
    labelContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(0.25),
      paddingBottom: theme.spacing(2.5),
      flex: 1,
      position: 'relative',
      zIndex: 1500
    },
    labelContainerWithType: {
      // Cards with both a title and project-type subtitle need a bit more
      // breathing room above the progress bar so the subtitle doesn't crowd it.
      paddingBottom: theme.spacing(3.5)
    },
    labelSingleDay: {
      overflow: 'hidden',
      paddingRight: '2px',
      paddingLeft: theme.spacing(1.25),
      minWidth: 0
    },
    labelMultiDay: {
      minWidth: 0,
      // overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start'
    },
    labelInnerContainer: {
      position: 'sticky',
      left: theme.spacing(1.25),
      backgroundColor: 'transparent'
    },
    innerSingleDay: {
      overflow: 'hidden',
      maxWidth: '100%',
      minWidth: 0,
      width: '100%',
      left: 0
    },
    innerMultiDay: {
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden'
    },
    singleDayText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    multiDayText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    typeText: {
      textTransform: 'capitalize'
    },
    checkIcon: {
      width: 14,
      height: 14
    },
    progressBarContainer: {
      position: 'absolute',
      left: theme.spacing(1),
      right: theme.spacing(2),
      bottom: theme.spacing(1),
      height: 10,
      borderRadius: 999,
      overflow: 'hidden',
      backgroundColor: ({ track }) => track,
      zIndex: 2000
    },
    progressBar: {
      height: '100%',
      borderRadius: 999,
      transition: 'all 0.3s ease',
      backgroundColor: ({ progressFill }) => progressFill
    },
    leftResizeHandle: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: theme.spacing(1.5),
      cursor: 'ew-resize',
      zIndex: 2000
    },
    rightResizeHandle: {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: theme.spacing(1.5),
      cursor: 'ew-resize',
      zIndex: 2000
    },
    title: {
      display: 'block'
    },
    tooltip: {
      display: 'block',
      height: '100%',
      width: '100%'
    }
  }),
  { name: 'ProjectBlock' }
)

export interface ProjectBlockProps {
  classes?: StyleClasses<typeof useStyles>
  event?: CalendarEvent
  onHover: (eventId: string | null) => void
  isHovered: boolean
  color: string // Kept for backwards compatibility but now using status color
  onEventUpdate?: (updatedEvent: CalendarEvent) => void
  cellWidth: number // Width of each date cell in pixels
  zoomLevel?: ZoomLevel // Optional zoom level to control text visibility
  extendsBeforeView?: boolean // True when the block was clamped because it started before the visible window
}

function ProjectBlock(props: ProjectBlockProps) {
  const { event, onHover, isHovered, onEventUpdate, cellWidth, zoomLevel = 'days', extendsBeforeView } = props
  const { options } = useClientOptions()
  const { events, setEvents, activeProjectBlockId, setActiveProjectBlockId } = useProductionCalendar()
  const crews = getCrews({ options, includeTrashed: true })
  const crewColorForPalette = crews.find((c) => c._id === event.crew)?.color
  const palette = getCrewPalette(crewColorForPalette)
  const classes = useStyles(palette)
  const isClicked = activeProjectBlockId === event.id
  const [isResizing, setIsResizing] = useState(false)
  const [_isMobile, setIsMobile] = useState(false)
  const isMobileViewport = useIsMobile()
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const tempEvent = events.find((ev) => ev.id === event.id)
  const livePopperEvent = selectedEvent ? events.find((ev) => ev.id === selectedEvent.id) ?? selectedEvent : null

  // Check the URL and if eventId is in there we will set it as outlined until we reset it
  const urlParams = new URLSearchParams(window.location.search)
  const [highlightEventId, setHighlightEventId] = useState(urlParams.get('eventId') === event.id)
  if (highlightEventId) {
    urlParams.delete('eventId')
    const newUrl =
      window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '') + window.location.hash
    window.history.replaceState({}, '', newUrl)
  }

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const { permissionToViewDetails, permissionToEdit } = useProductionEventPermissions(event)

  // Long-press to drag on touch devices. react-dnd-touch-backend's built-in
  // delayTouchStart is unusable (see EventsAgendaView _touchBackendOptions
  // comment), so we gate canDrag with a hold timer instead. A quick swipe
  // that starts on a card now scrolls the page; only an intentional press
  // picks the card up.
  const LONG_PRESS_MS = 250
  const SCROLL_CANCEL_PX = 8
  const [dragEnabled, setDragEnabled] = useState(false)
  const longPressTimerRef = useRef<number | null>(null)
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null)
  const isTouchingRef = useRef(false)

  const cancelLongPress = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!permissionToEdit || isResizing) return
    const t = e.touches[0]
    if (!t) return
    isTouchingRef.current = true
    touchStartPosRef.current = { x: t.clientX, y: t.clientY }
    cancelLongPress()
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTimerRef.current = null
      setDragEnabled(true)
      try {
        navigator.vibrate?.(15)
      } catch {
        // ignore — unsupported on iOS Safari / desktop
      }
    }, LONG_PRESS_MS)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (longPressTimerRef.current === null || !touchStartPosRef.current) return
    const t = e.touches[0]
    if (!t) return
    const dx = Math.abs(t.clientX - touchStartPosRef.current.x)
    const dy = Math.abs(t.clientY - touchStartPosRef.current.y)
    if (dx > SCROLL_CANCEL_PX || dy > SCROLL_CANCEL_PX) {
      cancelLongPress()
    }
  }

  const handleTouchEnd = () => {
    cancelLongPress()
    isTouchingRef.current = false
    touchStartPosRef.current = null
    setDragEnabled(false)
  }

  useEffect(() => () => cancelLongPress(), [])

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'event',
    item: { eventId: event.id, event },
    canDrag: () => !isResizing && permissionToEdit && (isTouchingRef.current ? dragEnabled : true),
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  // Suppress the native browser drag ghost (the Safari globe icon) by replacing
  // it with a transparent 1×1 image. CustomDragLayer renders the actual preview.
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview])

  const isComplete = event.progress === 100

  const handleMouseEnter = (_e: React.MouseEvent) => {
    onHover(event.id)
  }

  const handleMouseLeave = () => {
    onHover(null)
  }

  // Resize handlers for entire event
  const handleResizeStart = (e: React.MouseEvent, side: 'left' | 'right') => {
    e.stopPropagation()
    e.preventDefault()
    setIsResizing(true)

    const startX = e.clientX
    const currentStart = new Date(event.start!)
    const currentEnd = new Date(event.end!)

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault()
      moveEvent.stopPropagation()

      const deltaX = moveEvent.clientX - startX
      // Calculate how many cells (dates) we've moved
      const cellsDelta = Math.round(deltaX / cellWidth)

      if (cellsDelta === 0) return

      if (side === 'left') {
        const newStart = new Date(currentStart)
        newStart.setDate(newStart.getDate() + cellsDelta)

        // Ensure new start is before or on the same day as current end (minimum 1 day duration)
        if (newStart <= currentEnd) {
          const newEvents = events.map((ev) => {
            if (ev.id === event.id) {
              return {
                ...ev,
                start: newStart
              }
            }
            return ev
          })
          tempEvent.start = newStart
          setEvents(newEvents)
        }
      } else {
        const newEnd = new Date(currentEnd)
        newEnd.setDate(newEnd.getDate() + cellsDelta)

        // Ensure new end is on the same day or after current start (minimum 1 day duration)
        if (newEnd >= currentStart) {
          const newEvents = events.map((ev) => {
            if (ev.id === event.id) {
              return {
                ...ev,
                end: newEnd
              }
            }
            return ev
          })
          tempEvent.end = newEnd
          setEvents(newEvents)
        }
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      if (onEventUpdate) {
        onEventUpdate(tempEvent)
      }
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const closePopper = () => {
    setSelectedEvent(null)
    setAnchorEl(null)
    setActiveProjectBlockId(null)
  }

  // Close the popper when the user clicks outside the block and outside the popper.
  // Clicks on the source block are handled by handleMarkerClick (toggle/switch); clicks
  // inside MUI overlays opened from the popper (crew Menu, Delete ConfirmationDialog)
  // portal to document.body so we ignore them via their root class.
  // On mobile the popper is rendered as a Drawer (a Modal with its own backdrop),
  // so outside-click closing is already handled natively — running this listener
  // there would race the Drawer's child clicks (delete, crew dropdown) and unmount
  // it before they can fire.
  useEffect(() => {
    if (!isClicked) return
    if (isMobileViewport) return
    const handleDocMouseDown = (e: MouseEvent) => {
      if (isResizing) return
      const target = e.target as HTMLElement | null
      if (!target) return
      if (anchorEl?.contains(target)) return
      const popperEl = document.getElementById(`event-preview-popper-${event.id}`)
      if (popperEl?.contains(target)) return
      // JSS appends a numeric suffix to class names in this app's setup (e.g.
      // `MuiPopover-root-305`), so an exact `.MuiPopover-root` selector misses
      // them. Substring match catches both the bare and suffixed variants.
      if (
        target.closest?.(
          '[class*="MuiPopover-root"], [class*="MuiMenu-root"], [class*="MuiDialog-root"], [class*="MuiModal-root"], [class*="MuiDrawer-root"]'
        )
      )
        return
      closePopper()
    }
    document.addEventListener('mousedown', handleDocMouseDown)
    return () => document.removeEventListener('mousedown', handleDocMouseDown)
  }, [isClicked, isResizing, event.id, isMobileViewport, anchorEl])

  const handleMarkerClick = (event: CalendarEvent, clickEvent: React.MouseEvent) => {
    setHighlightEventId(null)
    clickEvent.stopPropagation()
    if (isClicked) {
      closePopper()
      return
    }
    setSelectedEvent(event)
    setAnchorEl(clickEvent.currentTarget as HTMLElement)
    setActiveProjectBlockId(event.id)
  }

  // Show text labels only in days view
  const showLabels = zoomLevel === 'days'
  const projectTypeLabel = getProjectTypeLabel(event.projectType, options)
  const hasProjectType = Boolean(projectTypeLabel)

  // Check if job is only one day
  const isSingleDay = event.start && event.end && moment(event.start).isSame(moment(event.end), 'day')

  return (
    <>
      <div
        ref={drag}
        style={{
          backgroundColor: 'transparent',
          opacity: isDragging ? 0 : 1,
          zIndex: isClicked ? 20 : isHovered ? 10 : 1,
          border: highlightEventId ? `2px solid ${palette.progressFill}` : 'none',
          borderRadius: highlightEventId ? 4 : 0,
          cursor: !permissionToViewDetails ? 'default' : undefined
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onClick={!permissionToViewDetails ? undefined : (e) => handleMarkerClick(event, e)}
        className={classes.root}
      >
        {!permissionToViewDetails && <LockIcon className={classes.lockIcon} />}
        {/* Single job block */}
        <div className={classes.jobBlockContainer}>
          <div
            className={classNames({
              [classes.jobBlock]: true,
              [classes.viewOnlyOverlay]: !permissionToViewDetails
            })}
            style={{
              boxShadow: isClicked
                ? `0 0 0 3px rgba(255, 255, 255, 0.9), 0 0 0 5px ${palette.progressFill}, 0 6px 20px rgba(0, 0, 0, 0.25)`
                : isHovered
                  ? '0 4px 12px rgba(0, 0, 0, 0.15)'
                  : 'none',
              ...(extendsBeforeView ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : null)
            }}
          >
            <Tooltip
              classes={{ badgeRoot: classes.tooltip }}
              hideIcon
              content={!permissionToViewDetails ? "You don't have permission to access this job." : ''}
            >
              <>
                {/* Client name and event type - only show in days view */}

                {showLabels && (
                  <div
                    className={classNames({
                      [classes.labelContainer]: true,
                      [classes.labelContainerWithType]: hasProjectType,
                      [isSingleDay ? classes.labelSingleDay : classes.labelMultiDay]: true
                    })}
                  >
                    <div
                      className={classNames({
                        [classes.labelInnerContainer]: true,
                        [isSingleDay ? classes.innerSingleDay : classes.innerMultiDay]: true
                      })}
                    >
                      <Tooltip content={permissionToViewDetails ? event.deal?.name ?? '' : ''} hideIcon disableWrapper>
                        <div
                          className={classNames({ [isSingleDay ? classes.singleDayText : classes.multiDayText]: true })}
                          // style={{ color: '#1F2E47', fontWeight: 600 }}
                        >
                          <Typography
                            variant={'h6'}
                            weight={'bold'}
                            className={classNames(classes.title, classes.titleLabel)}
                          >
                            {/* {event.contact.firstName} {event.contact.lastName} */}
                            {event.deal.name}
                            {isComplete && !isSingleDay && (
                              <CheckCircleIcon className={classes.checkIcon} style={{ color: palette.progressFill }} />
                            )}
                          </Typography>
                        </div>
                      </Tooltip>
                      <div
                        className={classNames({
                          [classes.typeText]: true,
                          [isSingleDay ? classes.singleDayText : classes.multiDayText]: true
                        })}
                      >
                        <Typography variant="subtitle1" className={classes.subtitleLabel}>
                          {projectTypeLabel}
                        </Typography>
                      </div>
                    </div>
                  </div>
                )}

                {/* Progress bar */}
                <div className={classes.progressBarContainer}>
                  <div className={classes.progressBar} style={{ width: `${event.progress ?? 0}%` }} />
                </div>
              </>
            </Tooltip>
          </div>
        </div>

        {/* Resize handles - invisible areas with cursor change only */}
        {(isHovered || isClicked) && !isDragging && permissionToEdit && (
          <>
            {/* Left resize handle */}
            <div
              className={classes.leftResizeHandle}
              style={{ pointerEvents: 'auto' }}
              onMouseDown={(e) => handleResizeStart(e, 'left')}
            />

            {/* Right resize handle */}
            <div
              className={classes.rightResizeHandle}
              style={{ pointerEvents: 'auto' }}
              onMouseDown={(e) => handleResizeStart(e, 'right')}
            />
          </>
        )}
      </div>
      {isClicked && !isDragging && livePopperEvent && anchorEl && (
        <ProductionEventPopper event={livePopperEvent} anchorEl={anchorEl} onClose={closePopper} closeOnCrewChange />
      )}
    </>
  )
}

export default ProjectBlock
